import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ExternalLink,
  Pencil,
  Plus,
  PowerOff,
  Power,
  Search,
  Target,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  ContextHero,
  EmptyStateModern,
  PageContainer,
  SectionTitle,
} from '@/components/design-system';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  useAllActivites,
  useBulkActivateActivites,
  useBulkDeleteActivites,
  useDeleteActivite,
  checkActiviteFkUsage,
} from '@/hooks/queries/useActivites';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRole } from '@/hooks/useRole';
import { ActiviteFormDialog } from '@/components/forms/ActiviteFormDialog';
import { cn } from '@/lib/utils';
import type { ActiviteRow, CanalActivite } from '@/lib/database.types';

const CANAL_LABEL: Record<CanalActivite, string> = {
  B2B: 'B2B',
  B2C: 'B2C',
  Mixte: 'Mixte',
};

export function ActivitesGlobalPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const { data: allActivites = [], isLoading } = useAllActivites();
  const deleteOne = useDeleteActivite();
  const bulkActivate = useBulkActivateActivites();
  const bulkDelete = useBulkDeleteActivites();

  const [filterEntreprise, setFilterEntreprise] = useState('');
  const [filterCanal, setFilterCanal] = useState<CanalActivite | ''>('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);

  const [createOpen, setCreateOpen] = useState(false);
  const [editActivite, setEditActivite] = useState<ActiviteRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ActiviteRow | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allActivites.filter((a) => {
      if (filterEntreprise && a.entreprise_id !== filterEntreprise) return false;
      if (filterCanal && a.canal !== filterCanal) return false;
      if (filterActive === 'active' && !a.is_active) return false;
      if (filterActive === 'inactive' && a.is_active) return false;
      if (
        term &&
        !a.nom.toLowerCase().includes(term) &&
        !a.code.toLowerCase().includes(term) &&
        !(a.description ?? '').toLowerCase().includes(term)
      )
        return false;
      return true;
    });
  }, [allActivites, filterEntreprise, filterCanal, filterActive, search]);

  const entrepriseById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of entreprises) m.set(e.id, e.nom);
    return m;
  }, [entreprises]);

  const selectedActivites = useMemo(
    () => filtered.filter((a) => selectedIds.has(a.id)),
    [filtered, selectedIds],
  );

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((a) => a.id)));
  };

  const handleDeleteSingle = async (a: ActiviteRow): Promise<void> => {
    const usageMap = await checkActiviteFkUsage([a.id]);
    const usage = usageMap.get(a.id);
    if (usage && usage.total > 0) {
      toast.error(
        `Suppression refusée — ${a.nom} est utilisée par ${usage.total} référence(s) (op. : ${usage.operations}, paie. form. : ${usage.paiements_formateurs}, dettes : ${usage.dettes_fournisseurs}, recouv. : ${usage.recouvrement}). Désactivez plutôt.`,
      );
      setConfirmDelete(null);
      return;
    }
    try {
      await deleteOne.mutateAsync(a.id);
      toast.success('Activité supprimée');
      setConfirmDelete(null);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  const stats = useMemo(() => {
    const active = allActivites.filter((a) => a.is_active).length;
    return { total: allActivites.length, active, inactive: allActivites.length - active };
  }, [allActivites]);

  return (
    <PageContainer>
      <ContextHero
        variant="params"
        icon={<Target className="h-5 w-5" />}
        title="Activités"
        subtitle={`Toutes les activités du groupe — ${stats.active} active${stats.active > 1 ? 's' : ''} sur ${stats.total}`}
        actions={
          isAdmin && (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-zinc-800 px-3 text-sm font-semibold text-white shadow-sm hover:bg-zinc-900"
            >
              <Plus className="h-3.5 w-3.5" /> Nouvelle activité
            </button>
          )
        }
      />

      {/* Filtres */}
      <div className="mt-5 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Entreprise</label>
          <select
            value={filterEntreprise}
            onChange={(e) => setFilterEntreprise(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">— Toutes —</option>
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Canal</label>
          <div className="flex gap-1">
            {(['', 'B2B', 'B2C', 'Mixte'] as const).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilterCanal(c as CanalActivite | '')}
                className={cn(
                  'h-9 rounded-md border px-3 text-xs font-semibold transition-colors',
                  filterCanal === c
                    ? 'border-zinc-700 bg-zinc-100 text-zinc-900'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {c === '' ? 'Tous' : CANAL_LABEL[c as CanalActivite]}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Statut</label>
          <div className="flex gap-1">
            {(['all', 'active', 'inactive'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilterActive(s)}
                className={cn(
                  'h-9 rounded-md border px-3 text-xs font-semibold transition-colors',
                  filterActive === s
                    ? 'border-zinc-700 bg-zinc-100 text-zinc-900'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {s === 'all' ? 'Toutes' : s === 'active' ? 'Actives' : 'Inactives'}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-slate-500">Recherche</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Code, nom, description…"
              className="h-9 w-56 pl-7"
            />
          </div>
        </div>
        {(filterEntreprise || filterCanal || filterActive !== 'all' || searchInput) && (
          <button
            type="button"
            onClick={() => {
              setFilterEntreprise('');
              setFilterCanal('');
              setFilterActive('all');
              setSearchInput('');
            }}
            className="inline-flex h-9 items-center gap-1 rounded-md px-3 text-xs text-slate-500 hover:bg-slate-100"
          >
            <XCircle className="h-3 w-3" /> Effacer
          </button>
        )}
      </div>

      {/* Sticky bulk bar */}
      {selectedIds.size > 0 && isAdmin && (
        <div className="sticky top-2 z-20 mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-300 bg-zinc-50/95 px-4 py-2.5 shadow-md backdrop-blur">
          <span className="text-sm font-bold text-zinc-900">
            {selectedIds.size} activité{selectedIds.size > 1 ? 's' : ''} sélectionnée
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                await bulkActivate.mutateAsync({ ids: Array.from(selectedIds), is_active: true });
                toast.success(`${selectedIds.size} activée(s)`);
                setSelectedIds(new Set());
              }}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              <Power className="h-3.5 w-3.5" /> Activer
            </button>
            <button
              type="button"
              onClick={async () => {
                await bulkActivate.mutateAsync({ ids: Array.from(selectedIds), is_active: false });
                toast.success(`${selectedIds.size} désactivée(s)`);
                setSelectedIds(new Set());
              }}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-3 text-xs font-semibold text-white hover:bg-amber-700"
            >
              <PowerOff className="h-3.5 w-3.5" /> Désactiver
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulkDelete(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Supprimer
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-100"
            >
              <XCircle className="h-3 w-3" /> Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Tableau */}
      <div className="mt-5">
        <SectionTitle
          trailing={
            <span className="text-xs text-slate-500">
              {filtered.length} / {allActivites.length}
            </span>
          }
        >
          Activités
        </SectionTitle>
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {isLoading ? (
            <div className="space-y-1 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6">
              <EmptyStateModern
                variant="params"
                icon={<Target className="h-6 w-6" />}
                title="Aucune activité"
                description="Créez une activité pour démarrer."
                ctaLabel={isAdmin ? 'Nouvelle activité' : undefined}
                onCtaClick={isAdmin ? () => setCreateOpen(true) : undefined}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50/80 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                  <tr>
                    {isAdmin && (
                      <th className="px-3 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === filtered.length && filtered.length > 0}
                          onChange={toggleAll}
                          aria-label="Tout sélectionner"
                        />
                      </th>
                    )}
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Nom</th>
                    <th className="px-3 py-2 text-left">Entreprise</th>
                    <th className="px-3 py-2 text-left">Canal</th>
                    <th className="px-3 py-2 text-right">Ordre</th>
                    <th className="px-3 py-2 text-left">Statut</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => (
                    <tr
                      key={a.id}
                      className={cn(
                        'border-b border-slate-100 transition-colors hover:bg-slate-50',
                        !a.is_active && 'bg-slate-50/40 text-slate-500',
                      )}
                    >
                      {isAdmin && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(a.id)}
                            onChange={() => toggleSelect(a.id)}
                            aria-label={`Sélectionner ${a.nom}`}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2 font-mono text-xs">{a.code}</td>
                      <td className="px-3 py-2 font-semibold">{a.nom}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">
                        {entrepriseById.get(a.entreprise_id) ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px]">
                          {CANAL_LABEL[a.canal]}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{a.ordre ?? 0}</td>
                      <td className="px-3 py-2">
                        {a.is_active ? (
                          <Badge variant="success" className="text-[10px]">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-slate-400">
                            Inactive
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to={`/parametres/organisation/${a.entreprise_id}/activites/${a.id}/services`}
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 hover:bg-slate-50"
                            title="Voir services"
                          >
                            <ExternalLink className="h-3 w-3" /> Services
                          </Link>
                          {isAdmin && (
                            <>
                              <button
                                type="button"
                                onClick={() => setEditActivite(a)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                title="Modifier"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(a)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                                title="Supprimer"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ActiviteFormDialog
        open={createOpen || editActivite !== null}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditActivite(null);
          }
        }}
        activite={editActivite}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={confirmDelete ? `Supprimer l’activité « ${confirmDelete.nom} » ?` : 'Supprimer ?'}
        description="Action irréversible. Le système vérifie d’abord qu’aucune opération / dossier / facture / paiement n’est lié. Sinon, désactivez plutôt."
        loading={deleteOne.isPending}
        onConfirm={async () => {
          if (confirmDelete) await handleDeleteSingle(confirmDelete);
        }}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        destructive
        title={`Supprimer ${selectedIds.size} activité(s) ?`}
        description="Le système vérifie les FK avant suppression. Les activités référencées par des opérations/dossiers/factures seront refusées."
        loading={bulkDelete.isPending}
        onConfirm={async () => {
          try {
            const result = await bulkDelete.mutateAsync(Array.from(selectedIds));
            if (result.refused.length === 0) {
              toast.success(`${result.deleted.length} activité(s) supprimée(s)`);
            } else {
              const examples = result.refused
                .slice(0, 3)
                .map((r) => {
                  const act = selectedActivites.find((a) => a.id === r.id);
                  return `${act?.nom ?? r.id.slice(0, 8)} (${r.usage.total} réf.)`;
                })
                .join(', ');
              toast.warning(
                `${result.deleted.length} OK · ${result.refused.length} refusée(s) (FK) — ex: ${examples}`,
              );
            }
            setConfirmBulkDelete(false);
            setSelectedIds(new Set());
          } catch (e) {
            toast.error(`Échec : ${(e as Error).message}`);
          }
        }}
      />
    </PageContainer>
  );
}
